import { LogoutIcon, SwipeLoungeIcon } from "../../assets/Icons";
import { useNavigate } from "react-router-dom";
import routeConstant from "../../utils/routeConstant";
import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  CardButtonWrapper,
  CardDetailsWrapper,
  CardHeaderSection,
  CardName,
  CardPlanDescription,
  HeaderNavigation,
  MainSection,
  MainSectionHeading,
  MainSectionSubHeading,
  MainSectionSubHeadingWrapper,
  MainSectionWrapper,
} from "../subscription/style";
import { CustomButton } from "../../globalStyle";
import { CardContainer } from "../../components/dashboardSections/style";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  notificationListSelector,
  notificationLoaderSelector,
} from "../../store/metadata/selector";
import { useEffect } from "react";
import { userDetailsSelector } from "../../store/account/selector";
import { logoutService } from "../../services/AuthServices";
import { AxiosResponse } from "axios";

const InvitedMemberPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationList = useSelector(notificationListSelector);
  const loading = useSelector(notificationLoaderSelector);
  const inviteTeamNotification = notificationList?.find(
    (notification: any) => notification?.type === "TeamInvite"
  );
  const userDetails =
    useSelector(userDetailsSelector) ??
    JSON.parse(localStorage.getItem("User") || "{}");
  const isInviteDeclined = userDetails?.isInvitedBySeat === "REJECTED";
  const isInviteAccepted = userDetails?.isInvitedBySeat === "ACCEPTED";

  useEffect(() => {
    if (userDetails?._id) {
      if (isInviteAccepted) {
        navigate(routeConstant.dashboard);
      } else if (isInviteDeclined) {
        navigate(routeConstant.subscription, { replace: true });
      } else {
        return;
      }
    }
  }, [userDetails]);

  useEffect(() => {
    dispatch(MetaDataSagaActions.notificationList());
  }, []);

  const handleAccept = () => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        MetaDataSagaActions.joinTeamMemberSeatSaga({
          notificationId: inviteTeamNotification?._id,
          teamId: inviteTeamNotification?.teamId,
          seatId: inviteTeamNotification?.seatId,
          status: "ACCEPTED",
          email: userDetails?.email,
          planType: userDetails?.planType,
          resolve,
          reject,
        })
      );
    })
      .then(() => {
        navigate(routeConstant.dashboard, { replace: true });
      })
      .catch((error) => {
        console.error("Accept failed:", error);
      });
  };

  const handleDecline = () => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        MetaDataSagaActions.joinTeamMemberSeatSaga({
          notificationId: inviteTeamNotification?._id,
          teamId: inviteTeamNotification?.teamId,
          seatId: inviteTeamNotification?.seatId,
          status: "REJECTED",
          resolve,
          reject,
        })
      );
    })
      .then(() => {
        navigate(routeConstant.subscription, { replace: true });
      })
      .catch((error) => {
        console.error("Decline failed:", error);
      });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
          marginTop: "1.875rem",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Logout function
  const handleLogout = async () => {
    const response = (await logoutService()) as AxiosResponse;
    if (response?.status === 200) {
      localStorage.clear();
      navigate(`/${routeConstant.login}`, { replace: true });
    }
  };

  return (
    <div style={{ margin: "1.875rem 10rem" }}>
      <HeaderNavigation>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* Left Icon */}
          <SwipeLoungeIcon />

          {/* Logout Button */}
          <CustomButton
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              borderRadius: "50px",
              background: theme.colors.red ?? "#ff4d4f",
              color: "#fff",
              fontWeight: 500,
              fontSize: "1rem",
              transition: "background 0.3s",
              ":hover": {
                background: theme.colors.redDark ?? "#e04344",
              },
            }}
            onClick={handleLogout}
          >
            {/* Optional Logout Icon */}
            <LogoutIcon />
            Logout
          </CustomButton>
        </Box>
      </HeaderNavigation>

      {inviteTeamNotification?._id ? (
        <>
          <MainSection>
            <MainSectionWrapper>
              <MainSectionHeading>
                You’ve been invited to join a workspace
              </MainSectionHeading>
            </MainSectionWrapper>
            <MainSectionSubHeadingWrapper>
              <MainSectionSubHeading>
                You have received an invitation from your admin to join their
                workspace and collaborate within the workspace.
              </MainSectionSubHeading>
            </MainSectionSubHeadingWrapper>
          </MainSection>
          <CardContainer
            sx={{
              padding: "1.875rem",
              background: theme.colors.richBlack ?? "#081720",
              border: `1px solid ${theme.colors.lightBlue}`,
              backgroundImage: 'url("/assets/images/Ellipse.svg")',
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              cursor: "default",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <CardHeaderSection>
              <CardName>{inviteTeamNotification?.title}</CardName>
              <CardPlanDescription
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <p>
                  By accepting this invitation, you’ll gain full access to the
                  team’s tools, shared resources, and project features under the
                  existing team plan.
                </p>
                <p>
                  If you decline, you’ll need to subscribe to your own plan to
                  access the platform independently.
                </p>
              </CardPlanDescription>
            </CardHeaderSection>

            <CardDetailsWrapper>
              <Box display="flex" justifyContent="center" gap={2}>
                <CardButtonWrapper style={{ justifyContent: "center" }}>
                  <CustomButton
                    sx={{
                      ":disabled": { color: "white" },
                      padding: "0.813rem 1.25rem",
                      color: theme.colors.white,
                      borderRadius: "100px",
                      border: "1px solid rgba(255, 255, 255, 0.4)",
                      background: theme.colors.backgroundPrimary,
                    }}
                    onClick={handleAccept}
                  >
                    Accept Invitation
                  </CustomButton>
                </CardButtonWrapper>

                <CardButtonWrapper style={{ justifyContent: "center" }}>
                  <CustomButton
                    sx={{
                      ":disabled": { color: "white" },
                      padding: "0.813rem 1.25rem",
                      color: theme.colors.white,
                      borderRadius: "100px",
                      border: "1px solid rgba(255, 255, 255, 0.4)",
                      background: "none",
                    }}
                    onClick={handleDecline}
                  >
                    Decline Invitation
                  </CustomButton>
                </CardButtonWrapper>
              </Box>
            </CardDetailsWrapper>
          </CardContainer>
        </>
      ) : (
        <MainSection>
          <MainSectionWrapper>
            <MainSectionHeading>No Invitation Available</MainSectionHeading>
          </MainSectionWrapper>
          <MainSectionSubHeadingWrapper>
            <MainSectionSubHeading>
              You currently do not have any invitations to join a team. If you
              were expecting an invite, please contact your administrator.
              <MainSectionSubHeading>
                Otherwise, you can create your own workspace by choosing a plan
                to get started.
              </MainSectionSubHeading>
            </MainSectionSubHeading>
          </MainSectionSubHeadingWrapper>
        </MainSection>
      )}
    </div>
  );
};

export default InvitedMemberPage;
